
import { DataScienceCourse } from './types';

export const DATASCIENCE_INTERMEDIATE_COURSE: DataScienceCourse = {
  id: 'datascience-intermediate',
  title: 'Intermediate Data Science',
  description: 'Advance your data science skills with machine learning pipelines, advanced analytics, and big data tools. Build production-ready solutions.',
  level: 'intermediate',
  totalDuration: '16-20 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Completed Data Science Fundamentals course',
    'Comfortable with Python programming',
    'Understanding of basic statistics',
    'Experience with Pandas and NumPy'
  ],
  learningOutcomes: [
    'Build end-to-end machine learning pipelines',
    'Perform advanced statistical analysis and A/B testing',
    'Create interactive dashboards and visualizations',
    'Handle time series data and forecasting',
    'Work with big data tools and frameworks',
    'Implement feature engineering techniques',
    'Design and analyze experiments',
    'Deploy models for business applications'
  ],
  modules: [
    {
      id: 'ds-advanced-stats',
      title: 'Advanced Statistics & A/B Testing',
      description: 'Master hypothesis testing, confidence intervals, A/B testing methodology, and statistical significance for business decisions.',
      duration: '2.5 hours',
      difficulty: 'intermediate',
      objectives: [
        'Design and analyze A/B tests',
        'Calculate statistical significance and power',
        'Handle multiple testing problems',
        'Interpret business impact of statistical findings'
      ],
      content: `# Advanced Statistics & A/B Testing

Advanced statistical methods are crucial for making data-driven business decisions and validating hypotheses in real-world scenarios.

## Hypothesis Testing Deep Dive

### Type I and Type II Errors
- **Type I Error (α)**: Rejecting true null hypothesis (false positive)
- **Type II Error (β)**: Accepting false null hypothesis (false negative)
- **Statistical Power (1-β)**: Probability of correctly rejecting false null

### Effect Size
- Measures practical significance vs statistical significance
- Cohen's d for means, odds ratio for proportions
- Important for determining business impact

## A/B Testing Framework

### 1. Planning Phase
- Define hypothesis and success metrics
- Calculate required sample size
- Determine test duration
- Plan randomization strategy

### 2. Implementation
- Random assignment to control/treatment
- Ensure proper tracking and data collection
- Monitor for external factors

### 3. Analysis
- Check assumptions (normality, equal variance)
- Calculate statistical significance
- Estimate confidence intervals
- Assess practical significance

### 4. Decision Making
- Consider business context
- Account for multiple testing
- Plan follow-up experiments

## Common A/B Testing Pitfalls

### Multiple Testing Problem
- Running multiple tests increases false positive rate
- Solutions: Bonferroni correction, FDR control
- Pre-planned vs post-hoc comparisons

### Selection Bias
- Ensure proper randomization
- Check for systematic differences between groups
- Account for dropouts and missing data

### Temporal Effects
- Seasonality and trends
- Learning effects
- External events impact

## Advanced Statistical Tests

### Mann-Whitney U Test
- Non-parametric alternative to t-test
- Use when data is not normally distributed
- Compares distributions rather than means

### Chi-Square Test of Independence
- Tests relationship between categorical variables
- Examines contingency tables
- Calculates expected vs observed frequencies

### ANOVA (Analysis of Variance)
- Compare means across multiple groups
- One-way vs two-way ANOVA
- Post-hoc tests for pairwise comparisons`,
      codeExamples: [
        {
          title: 'A/B Testing Analysis Framework',
          language: 'python',
          code: `import numpy as np
import pandas as pd
from scipy import stats
import matplotlib.pyplot as plt
import seaborn as sns

# Simulate A/B test data
np.random.seed(42)

# Control group: 10% conversion rate
control_size = 1000
control_conversions = np.random.binomial(control_size, 0.10)

# Treatment group: 12% conversion rate (20% relative improvement)
treatment_size = 1000
treatment_conversions = np.random.binomial(treatment_size, 0.12)

# Create dataset
data = pd.DataFrame({
    'group': ['control'] * control_size + ['treatment'] * treatment_size,
    'converted': ([1] * control_conversions + [0] * (control_size - control_conversions) +
                 [1] * treatment_conversions + [0] * (treatment_size - treatment_conversions))
})

# Calculate conversion rates
conversion_rates = data.groupby('group')['converted'].agg(['sum', 'count', 'mean'])
conversion_rates['conversion_rate'] = conversion_rates['mean']
print("Conversion Rates by Group:")
print(conversion_rates)

# Statistical test - Two-proportion z-test
def two_proportion_z_test(success1, n1, success2, n2):
    p1 = success1 / n1
    p2 = success2 / n2
    p_pool = (success1 + success2) / (n1 + n2)
    
    se = np.sqrt(p_pool * (1 - p_pool) * (1/n1 + 1/n2))
    z = (p1 - p2) / se
    p_value = 2 * (1 - stats.norm.cdf(abs(z)))
    
    return z, p_value, p1, p2

z_stat, p_value, p_control, p_treatment = two_proportion_z_test(
    control_conversions, control_size,
    treatment_conversions, treatment_size
)

print(f"\\nA/B Test Results:")
print(f"Control conversion rate: {p_control:.3f}")
print(f"Treatment conversion rate: {p_treatment:.3f}")
print(f"Relative improvement: {((p_treatment - p_control) / p_control * 100):.1f}%")
print(f"Z-statistic: {z_stat:.4f}")
print(f"P-value: {p_value:.4f}")

# Confidence interval for difference
diff = p_treatment - p_control
se_diff = np.sqrt(p_control * (1 - p_control) / control_size + 
                  p_treatment * (1 - p_treatment) / treatment_size)
ci_lower = diff - 1.96 * se_diff
ci_upper = diff + 1.96 * se_diff

print(f"\\n95% Confidence Interval for difference: [{ci_lower:.4f}, {ci_upper:.4f}]")

# Visualization
plt.figure(figsize=(10, 6))
x = ['Control', 'Treatment']
y = [p_control, p_treatment]
colors = ['lightcoral', 'lightblue']

bars = plt.bar(x, y, color=colors, alpha=0.7)
plt.ylabel('Conversion Rate')
plt.title('A/B Test Results: Conversion Rates by Group')

# Add value labels on bars
for bar, value in zip(bars, y):
    plt.text(bar.get_x() + bar.get_width()/2, bar.get_height() + 0.001,
             f'{value:.3f}', ha='center', va='bottom')

# Add error bars for confidence intervals
control_ci = 1.96 * np.sqrt(p_control * (1 - p_control) / control_size)
treatment_ci = 1.96 * np.sqrt(p_treatment * (1 - p_treatment) / treatment_size)

plt.errorbar(x, y, yerr=[control_ci, treatment_ci], fmt='none', color='black', capsize=5)

plt.tight_layout()
plt.show()

# Statistical significance interpretation
alpha = 0.05
if p_value < alpha:
    print(f"\\nResult: STATISTICALLY SIGNIFICANT (p < {alpha})")
    print("We can reject the null hypothesis. There is evidence of a difference.")
else:
    print(f"\\nResult: NOT STATISTICALLY SIGNIFICANT (p >= {alpha})")
    print("We fail to reject the null hypothesis. No significant difference detected.")`,
          explanation: 'This comprehensive example shows how to analyze A/B test results, calculate statistical significance, and interpret business impact.'
        }
      ]
    },
    {
      id: 'ds-ml-pipeline',
      title: 'Machine Learning Pipeline Development',
      description: 'Learn to build robust, scalable machine learning pipelines with proper validation, feature engineering, and model selection.',
      duration: '3 hours',
      difficulty: 'intermediate',
      objectives: [
        'Design end-to-end ML pipelines',
        'Implement cross-validation strategies',
        'Master hyperparameter tuning',
        'Handle model deployment considerations'
      ],
      content: `# Machine Learning Pipeline Development

A well-designed ML pipeline ensures reproducible, scalable, and maintainable machine learning solutions.

## Pipeline Components

### 1. Data Ingestion
- Automated data loading from various sources
- Data validation and quality checks
- Handling streaming vs batch data

### 2. Data Preprocessing
- Missing value imputation
- Outlier detection and treatment
- Feature scaling and normalization
- Categorical encoding

### 3. Feature Engineering
- Feature creation and selection
- Dimensionality reduction
- Feature interactions
- Domain-specific transformations

### 4. Model Training
- Algorithm selection
- Hyperparameter optimization
- Cross-validation
- Model ensembling

### 5. Model Evaluation
- Performance metrics selection
- Validation strategies
- Bias and variance analysis
- Model interpretability

### 6. Model Deployment
- Model serialization
- API development
- Monitoring and maintenance
- A/B testing for models

## Cross-Validation Strategies

### K-Fold Cross-Validation
- Split data into k equal parts
- Train on k-1 parts, validate on 1
- Repeat k times, average results

### Stratified K-Fold
- Maintains class distribution in each fold
- Important for imbalanced datasets
- Ensures representative validation

### Time Series Cross-Validation
- Respects temporal order
- Forward chaining validation
- Prevents data leakage

## Hyperparameter Optimization

### Grid Search
- Exhaustive search over parameter grid
- Computationally expensive but thorough
- Good for small parameter spaces

### Random Search
- Randomly sample parameter combinations
- More efficient than grid search
- Often finds good solutions faster

### Bayesian Optimization
- Uses probabilistic model to guide search
- Efficient for expensive evaluations
- Balances exploration vs exploitation

## Model Selection Criteria

### Performance Metrics
- **Classification**: Accuracy, Precision, Recall, F1-score, AUC-ROC
- **Regression**: MAE, MSE, RMSE, R-squared
- **Business Metrics**: Revenue impact, cost savings

### Model Complexity
- Bias-variance tradeoff
- Overfitting vs underfitting
- Interpretability requirements

### Computational Requirements
- Training time
- Prediction latency
- Memory usage
- Scalability needs`
    }
  ]
};
